import { useMemo } from 'react';
import {
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { usePatchLoanDscrMutation } from '@/apis/extended/loanApplicationApi';
import type { DscrFields, LoanApplication } from '@/types/LoanApplicationType';
import { parseLoanPatchError } from './loanSectionForm';
import { LoanSectionFormFooter } from './LoanSectionFormFooter';
import { useLoanSectionForm } from './useLoanSectionForm';

type Props = {
  application: LoanApplication;
  readOnly?: boolean;
};

const emptyDscr: DscrFields = {
  monthlyRent: null,
  rentStabilized: false,
  monthlyTaxes: null,
  monthlyInsurance: null,
  monthlyHoa: null,
  propertyManagementPct: null,
  otherMonthlyExpenses: null,
  loanAmount: null,
  interestRateAnnualPct: null,
  amortizationMonths: 360,
  annualDebtServiceOverride: null,
  reservesMonths: null,
};

export function DscrSection({ application, readOnly }: Props) {
  const [patch, { isLoading }] = usePatchLoanDscrMutation();

  const initial = useMemo(
    () => ({ ...emptyDscr, ...(application.dscrFields || {}) }),
    [application._id, application.dscrFields, application.updatedAt]
  );

  const {
    draft: local,
    setDraft: setLocal,
    isDirty,
    markSaved,
    resetDraft,
    readOnly: formReadOnly,
  } = useLoanSectionForm({
    sectionKey: 'dscr',
    applicationId: application._id,
    applicationUpdatedAt: application.updatedAt,
    initialValue: initial,
    readOnly,
  });

  const saveSection = async () => {
    if (formReadOnly) return;
    try {
      await patch({
        id: application._id,
        dscrFields: local,
      }).unwrap();
      markSaved(local);
      toast.success('Saved — metrics updated');
    } catch (e: unknown) {
      toast.error(parseLoanPatchError(e));
    }
  };

  const c = application.dscrFields?.computed;
  const disabled = formReadOnly || isLoading;

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        DSCR inputs feed internal NOI and debt-service estimates (no external
        pricing).
      </Typography>
      {c && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" gutterBottom>
            Computed metrics
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                NOI (annual)
              </Typography>
              <Typography fontWeight={600}>
                {c.noi != null ? `$${c.noi.toFixed(0)}` : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Debt service (annual)
              </Typography>
              <Typography fontWeight={600}>
                {c.annualDebtService != null
                  ? `$${c.annualDebtService.toFixed(0)}`
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                DSCR
              </Typography>
              <Typography fontWeight={600}>
                {c.dscr != null ? c.dscr.toFixed(3) : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Monthly rent (underwriting)"
            type="number"
            value={local.monthlyRent ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                monthlyRent:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Monthly taxes"
            type="number"
            value={local.monthlyTaxes ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                monthlyTaxes:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Monthly insurance"
            type="number"
            value={local.monthlyInsurance ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                monthlyInsurance:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Monthly HOA"
            type="number"
            value={local.monthlyHoa ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                monthlyHoa:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Property mgmt % of rent"
            type="number"
            value={local.propertyManagementPct ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                propertyManagementPct:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Other monthly expenses"
            type="number"
            value={local.otherMonthlyExpenses ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                otherMonthlyExpenses:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Loan amount"
            type="number"
            value={local.loanAmount ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                loanAmount:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Note rate (annual %)"
            type="number"
            value={local.interestRateAnnualPct ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                interestRateAnnualPct:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Amortization (months)"
            type="number"
            value={local.amortizationMonths ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                amortizationMonths:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Annual debt service override (optional)"
            type="number"
            value={local.annualDebtServiceOverride ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                annualDebtServiceOverride:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
            helperText="When set, replaces P&I-based annual debt service."
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Reserves (months)"
            type="number"
            value={local.reservesMonths ?? ''}
            disabled={disabled}
            onChange={(e) =>
              setLocal((s) => ({
                ...s,
                reservesMonths:
                  e.target.value === '' ? null : Number(e.target.value),
              }))
            }
          />
        </Grid>
      </Grid>
      <LoanSectionFormFooter
        readOnly={formReadOnly}
        isDirty={isDirty}
        isSaving={isLoading}
        saveLabel="Save DSCR inputs"
        onSave={() => void saveSection()}
        onCancel={resetDraft}
      />
    </div>
  );
}
